// Compact header search: an icon in the header toolbar that opens a small
// search panel with the first product results from Shopify's predictive
// search API and an "all results" link to the search page.

const DEBOUNCE_MS = 220;
const MIN_CHARS = 2;

customElements.define('header-search', class extends HTMLElement {
    connectedCallback() {
        this.$toggle = this.querySelector('[data-element="toggle"]');
        this.$panel = this.querySelector('[data-element="panel"]');
        this.$form = this.querySelector('[data-element="form"]');
        this.$input = this.querySelector('[data-element="input"]');
        this.$close = this.querySelector('[data-element="close"]');
        this.$results = this.querySelector('[data-element="results"]');

        this.limit = Number(this.dataset.limit) || 5;
        this.root = window.Shopify?.routes?.root || '/';
        this.formatter = this._createFormatter();

        this._onDocClick = this._onDocClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);

        this.$toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.$close.addEventListener('click', () => this.close(true));
        this.$input.addEventListener('input', () => this._onInput());
    }

    disconnectedCallback() {
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onKeydown);
    }

    get isOpen() {
        return this.hasAttribute('open');
    }

    open() {
        this.setAttribute('open', '');
        this.$toggle.setAttribute('aria-expanded', 'true');
        this.$panel.hidden = false;
        requestAnimationFrame(() => this.$input.focus());
        document.addEventListener('click', this._onDocClick);
        document.addEventListener('keydown', this._onKeydown);
    }

    close(returnFocus = false) {
        this.removeAttribute('open');
        this.$toggle.setAttribute('aria-expanded', 'false');
        this.$panel.hidden = true;
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onKeydown);
        if (returnFocus) this.$toggle.focus();
    }

    _onDocClick(e) {
        if (!this.contains(e.target)) this.close();
    }

    _onKeydown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this.close(true);
        }
    }

    _onInput() {
        clearTimeout(this._timer);
        const term = this.$input.value.trim();

        if (term.length < MIN_CHARS) {
            this._abort();
            this._render('');
            return;
        }
        this._timer = setTimeout(() => this._search(term), DEBOUNCE_MS);
    }

    _abort() {
        this._controller?.abort();
        this._controller = null;
    }

    async _search(term) {
        this._abort();
        this._controller = new AbortController();
        this.setAttribute('loading', '');

        const params = new URLSearchParams({
            q: term,
            'resources[type]': 'product',
            'resources[limit]': String(this.limit),
            'resources[options][unavailable_products]': 'last'
        });

        try {
            const res = await fetch(`${this.root}search/suggest.json?${params}`, {
                signal: this._controller.signal
            });
            if (!res.ok) throw new Error(`Search failed: ${res.status}`);
            const data = await res.json();
            const products = data?.resources?.results?.products || [];
            this._render(this._resultsHTML(products, term));
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error(err);
                this._render('');
            }
        } finally {
            this.removeAttribute('loading');
        }
    }

    _render(html) {
        this.$results.innerHTML = html;
        this.$results.hidden = !html;
    }

    _resultsHTML(products, term) {
        const allUrl = `${this.root}search?${new URLSearchParams({ q: term, type: 'product' })}`;
        const allLink = `
            <a class="#header-search-all" href="${allUrl}">
                <span>${this._escape(this.dataset.allLabel)}</span>
                <span aria-hidden="true">→</span>
            </a>`;

        if (!products.length) {
            return `
                <p class="#header-search-empty">${this._escape(this.dataset.emptyLabel)} “${this._escape(term)}”</p>
                ${allLink}`;
        }

        const items = products.map((p) => {
            const img = p.featured_image?.url || p.image;
            const thumb = img
                ? `<img src="${this._sized(img, 120)}" alt="" width="48" height="48" loading="lazy">`
                : '';
            const onSale = Number(p.compare_at_price_max) > Number(p.price);
            const compare = onSale
                ? `<s class="#header-search-item-compare">${this._money(p.compare_at_price_max)}</s>`
                : '';

            return `
                <li>
                    <a class="#header-search-item" href="${p.url}">
                        <span class="#header-search-item-media">${thumb}</span>
                        <span class="#header-search-item-info">
                            ${p.vendor ? `<span class="#header-search-item-vendor">${this._escape(p.vendor)}</span>` : ''}
                            <span class="#header-search-item-title">${this._escape(p.title)}</span>
                        </span>
                        <span class="#header-search-item-price">${compare}${this._money(p.price)}</span>
                    </a>
                </li>`;
        }).join('');

        return `<ul class="#header-search-list" role="list">${items}</ul>${allLink}`;
    }

    _createFormatter() {
        const currency = window.Shopify?.currency?.active || 'EUR';
        const locale = document.documentElement.lang || 'nl-NL';
        try {
            return new Intl.NumberFormat(locale, { style: 'currency', currency });
        } catch {
            return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
        }
    }

    // suggest.json returns prices as decimal strings in the active currency.
    _money(value) {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatter.format(n) : '';
    }

    _sized(url, width) {
        return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
    }

    _escape(str = '') {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
});
